import { useEffect, useState } from 'react';
import type { VerificationViewDoc } from './verificationView';

export type SubscribeFn = (
  onNext: (docs: VerificationViewDoc[]) => void,
  onError: (error: Error) => void,
) => () => void;

/**
 * One listener on the whole `verification_views` collection (a few hundred small docs at most).
 * The Firestore SDK is loaded on first use: it is ~70 KB gzip that every other admin page would
 * otherwise download for nothing.
 */
export const firestoreSubscribe: SubscribeFn = (onNext, onError) => {
  let unsubscribe: () => void = () => {};
  let cancelled = false;
  Promise.all([import('firebase/firestore'), import('../config/firestore')])
    .then(([fs, { getDb }]) => {
      if (cancelled) return;
      unsubscribe = fs.onSnapshot(
        fs.collection(getDb(), 'verification_views'),
        (snapshot) =>
          onNext(
            snapshot.docs.map((d) => ({ ...(d.data() as VerificationViewDoc), specialistId: d.id })),
          ),
        (error) => onError(error),
      );
    })
    .catch((error) => onError(error instanceof Error ? error : new Error(String(error))));
  return () => {
    cancelled = true;
    unsubscribe();
  };
};

export interface VerificationViewsState {
  views: Map<string, VerificationViewDoc>;
  /** True once a snapshot has arrived and the listener has not failed since. */
  live: boolean;
  error: string | null;
}

/**
 * Live per-specialist verification views (no polling). The last known data is kept when the
 * listener fails, so the page degrades to "slightly stale" instead of going blank.
 */
export function useVerificationViews(subscribe: SubscribeFn = firestoreSubscribe): VerificationViewsState {
  const [state, setState] = useState<VerificationViewsState>({
    views: new Map(),
    live: false,
    error: null,
  });

  useEffect(() => {
    const unsubscribe = subscribe(
      (docs) =>
        setState({ views: new Map(docs.map((d) => [d.specialistId, d])), live: true, error: null }),
      (error) => setState((prev) => ({ ...prev, live: false, error: error.message })),
    );
    return unsubscribe;
  }, [subscribe]);

  return state;
}
